import DownloadIcon from "@mui/icons-material/Download"
import {
  Alert,
  Button,
  Card,
  CardContent,
  Chip,
  Link,
  Stack,
  Typography,
} from "@mui/material"
import { DataGrid, GridColDef } from "@mui/x-data-grid"
import { useState } from "react"
import { DateRangeFilter } from "../components/DateRangeFilter"
import {
  GroupFilterSelect,
  useGroupFilter,
} from "../components/GroupFilterSelect"
import { ReportPageHeader } from "../components/ReportPageHeader"
import { eventReportPage } from "../constants/reportPages"
import {
  exportEventsAsCSV,
  GetEventParams,
  getPaginationMeta,
  useGetEventsQuery,
} from "../services/api"
import { useAppSelector } from "../store/hooks"
import { selectActiveOrgId } from "../store/sessionSlice"
import {
  formatBoolean,
  formatDateTime,
  formatNullable,
  toIsoDateTime,
} from "./reportFormatters"

type EventDateFilters = Pick<GetEventParams, "start_date" | "end_date">

const columns: GridColDef[] = [
  {
    field: "title",
    headerName: "Title",
    width: 260,
    sortable: false,
    valueGetter: (_value, row) => formatNullable(row.title),
  },
  {
    field: "start_date",
    headerName: "Start",
    width: 170,
    sortable: false,
    valueGetter: (_value, row) => formatDateTime(row.start_date),
  },
  {
    field: "end_date",
    headerName: "End",
    width: 170,
    sortable: false,
    valueGetter: (_value, row) => formatDateTime(row.end_date),
  },
  {
    field: "type",
    headerName: "Type",
    width: 140,
    sortable: false,
    valueGetter: (_value, row) => formatNullable(row.type),
  },
  {
    field: "published",
    headerName: "Published",
    width: 120,
    sortable: false,
    renderCell: (params) => (
      <Chip
        label={params.row.published ? "Yes" : "No"}
        color={params.row.published ? "success" : "default"}
        size="small"
      />
    ),
  },
  {
    field: "draft",
    headerName: "Draft",
    width: 100,
    sortable: false,
    valueGetter: (_value, row) => formatBoolean(row.draft),
  },
  {
    field: "archived",
    headerName: "Archived",
    width: 110,
    sortable: false,
    valueGetter: (_value, row) => formatBoolean(row.archived),
  },
  {
    field: "all_day",
    headerName: "All Day",
    width: 100,
    sortable: false,
    valueGetter: (_value, row) => formatBoolean(row.all_day),
  },
  {
    field: "capacity",
    headerName: "Capacity",
    width: 110,
    type: "number",
    sortable: false,
    valueGetter: (_value, row) => formatNullable(row.capacity),
  },
  {
    field: "registration_start",
    headerName: "Registration Start",
    width: 180,
    sortable: false,
    valueGetter: (_value, row) => formatDateTime(row.registration_start),
  },
  {
    field: "registration_end",
    headerName: "Registration End",
    width: 180,
    sortable: false,
    valueGetter: (_value, row) => formatDateTime(row.registration_end),
  },
  {
    field: "meeting_link",
    headerName: "Meeting Link",
    width: 180,
    sortable: false,
    renderCell: (params) => {
      if (!params.row.meeting_link) {
        return "-"
      }

      return (
        <Link href={params.row.meeting_link} target="_blank" rel="noreferrer">
          Open
        </Link>
      )
    },
  },
  {
    field: "created",
    headerName: "Created",
    width: 170,
    sortable: false,
    valueGetter: (_value, row) => formatDateTime(row.created),
  },
  {
    field: "updated",
    headerName: "Updated",
    width: 170,
    sortable: false,
    valueGetter: (_value, row) => formatDateTime(row.updated),
  },
]

function EventReportPage() {
  const activeOrgId = useAppSelector(selectActiveOrgId)
  const { selectedGroups, setSelectedGroups, groupParams } =
    useGroupFilter(activeOrgId)
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  })
  const [validationError, setValidationError] = useState<string | null>(null)
  const [filters, setFilters] = useState({
    start_date: "",
    end_date: "",
  })
  const [appliedFilters, setAppliedFilters] = useState<
    Partial<EventDateFilters>
  >({})

  const queryParams = {
    ...appliedFilters,
    ...groupParams,
    limit: paginationModel.pageSize,
    offset: paginationModel.page * paginationModel.pageSize,
    org_id: activeOrgId,
  }

  const { data, error, isLoading } = useGetEventsQuery(queryParams)
  const paginationMeta = getPaginationMeta(data)

  const handleFilterChange = (
    field: "start_date" | "end_date",
    value: string
  ) => {
    setFilters((prev) => ({ ...prev, [field]: value }))
  }

  const handleApplyFilters = () => {
    const startMs = filters.start_date ? Date.parse(filters.start_date) : null
    const endMs = filters.end_date ? Date.parse(filters.end_date) : null

    if (startMs !== null && Number.isNaN(startMs)) {
      setValidationError("Start date is invalid.")
      return
    }

    if (endMs !== null && Number.isNaN(endMs)) {
      setValidationError("End date is invalid.")
      return
    }

    if (startMs !== null && endMs !== null && startMs > endMs) {
      setValidationError("Start date must be before end date.")
      return
    }

    const nextFilters: Partial<EventDateFilters> = {}

    if (startMs !== null) {
      nextFilters.start_date = toIsoDateTime(filters.start_date)
    }
    if (endMs !== null) {
      nextFilters.end_date = toIsoDateTime(filters.end_date)
    }

    setValidationError(null)
    setPaginationModel((prev) => ({ ...prev, page: 0 }))
    setAppliedFilters(nextFilters)
  }

  const handleClearFilters = () => {
    setSelectedGroups([])
    setValidationError(null)
    setPaginationModel({ page: 0, pageSize: 25 })
    setFilters({
      start_date: "",
      end_date: "",
    })
    setAppliedFilters({})
  }

  const handleExportCSV = () => {
    exportEventsAsCSV({
      ...appliedFilters,
      ...groupParams,
      org_id: activeOrgId,
    })
  }

  return (
    <Stack spacing={4}>
      <ReportPageHeader
        title={eventReportPage.pageTitle}
        description={eventReportPage.endpointDescription}
        action={
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleExportCSV}
            disabled={isLoading}
          >
            Export CSV
          </Button>
        }
      />

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Filters
          </Typography>
          <Stack
            direction="row"
            spacing={2}
            flexWrap="wrap"
            alignItems="flex-end"
          >
            <GroupFilterSelect
              organizationId={activeOrgId}
              value={selectedGroups}
              onChange={(value) => {
                setSelectedGroups(value)
                setPaginationModel((prev) => ({ ...prev, page: 0 }))
              }}
            />
            <DateRangeFilter
              label="Event Date Range"
              startDate={filters.start_date}
              endDate={filters.end_date}
              onStartDateChange={(value) =>
                handleFilterChange("start_date", value)
              }
              onEndDateChange={(value) => handleFilterChange("end_date", value)}
            />
            <Button variant="contained" onClick={handleApplyFilters}>
              Apply
            </Button>
            <Button variant="outlined" onClick={handleClearFilters}>
              Clear
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {validationError && <Alert severity="warning">{validationError}</Alert>}

      {error && (
        <Alert severity="error">Error loading events. Please try again.</Alert>
      )}

      <Card>
        <DataGrid
          rows={data?.data || []}
          getRowId={(row) => row.id ?? row.uuid}
          columns={columns}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          rowCount={paginationMeta?.total || paginationMeta?.count || 0}
          paginationMode="server"
          loading={isLoading}
          pageSizeOptions={[10, 25, 50, 100]}
          disableRowSelectionOnClick
          autoHeight
        />
      </Card>
    </Stack>
  )
}

export default EventReportPage
